import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { Icone } from '../components/Icone';
import { IconePleine } from '../components/IconePleine';
import { Chargement, MessageErreur, Pastille } from '../components/ui/Primitives';
import { useNotifications } from '../lib/useNotifications';
import {
  urlDocumentApprenant,
  heures,
  pourcentagePresence,
  useTableauBordApprenant,
  type CoursAVenir,
  type DevoirApprenant,
  type EcheanceApprenant,
  type RepriseFormation,
  type SeanceApprenant,
  type TableauBordApprenant,
} from '../lib/useEspaceApprenant';
import { adresseLecon } from '../lib/useLecteur';
import { BarreAvancement, tauxLisible } from '../components/lecteur/Progression';
import { dureeLisible } from '../lib/useElearning';
import { tempsLisible } from '../lib/temps-video';
import { adresseDevoir, echeanceLongue, jourEcheance, pastilleDevoir } from '../lib/affichage-devoirs';
import { ErreurApi } from '../lib/api';
import { MesTestsPositionnement } from '../components/tests/MesTestsPositionnement';

/**
 * Tableau de bord de l'espace apprenant (écran `dashboard`, maquette
 * `docs/maquette/modules/03-pages-dashboard-catalogue.js`).
 *
 * Chaque chiffre vient du dossier de l'apprenant connecté, relu à chaque
 * arrivée sur l'écran (`/espace-apprenant/tableau-bord`) : la prochaine leçon
 * à suivre (la reprise du lecteur), les prochains cours (le planning du
 * calendrier), les devoirs à rendre triés par date limite (la règle de
 * « Devoirs & évaluations »), les ECF, l'émargement et les documents. Rien
 * n'y est recalculé.
 *
 * Le taux de complétion est celui de « Mes formations » (TF-451) : leçons
 * terminées et quiz faits sur toute la formation ; la carte de reprise dit le
 * bloc du cours et sa progression. Les heures affichées sont les heures
 * **émargées**, jamais le volume prévu.
 */

/** Date civile `AAAA-MM-JJ` telle qu'elle doit se lire à La Réunion. */
function dateCourte(iso: string): string {
  const [annee, mois, jour] = iso.split('-');
  return `${jour}/${mois}/${annee}`;
}

function dateLongue(iso: string): string {
  const [annee, mois, jour] = iso.split('-').map(Number);
  if (!annee || !mois || !jour) return iso;
  return new Date(Date.UTC(annee, mois - 1, jour)).toLocaleDateString('fr-FR', {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Jour civil réunionnais d'un instant (maintenant par défaut), au format `AAAA-MM-JJ`. */
function jourReunion(instant = new Date()): string {
  return instant.toLocaleDateString('en-CA', { timeZone: 'Indian/Reunion' });
}

function heureCourte(horodatage: string): string {
  return new Date(horodatage).toLocaleString('fr-FR', {
    timeZone: 'Indian/Reunion',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** « dans 12 jours », « aujourd'hui », « il y a 3 jours » — sans arrondi trompeur. */
function delai(jours: number): string {
  if (jours === 0) return "aujourd'hui";
  if (jours === 1) return 'demain';
  if (jours > 1) return `dans ${jours} jours`;
  if (jours === -1) return 'hier';
  return `il y a ${Math.abs(jours)} jours`;
}

/** Jours civils réunionnais écoulés depuis un instant. */
function joursDepuis(horodatage: string): number {
  const utc = (jour: string) => Date.parse(`${jour}T00:00:00Z`);
  return Math.round((utc(jourReunion()) - utc(jourReunion(new Date(horodatage)))) / 86_400_000);
}

function tonEcheance(jours: number): 'danger' | 'warning' | 'info' | 'neutral' {
  if (jours < 0) return 'neutral';
  if (jours <= 7) return 'danger';
  if (jours <= 30) return 'warning';
  return 'info';
}

function pluriel(nombre: number, mot: string): string {
  return `${nombre} ${mot}${nombre > 1 ? 's' : ''}`;
}

function initiales(nom: string): string {
  return nom
    .split(' ')
    .map((partie) => partie[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** Phrase d'accueil, tirée des prochains cours et des devoirs du dossier. */
function PhraseAccueil({ data }: { data: TableauBordApprenant }): JSX.Element {
  const jours = data.prochainsCours.joursCetteSemaine;
  const { nombre, aRendre } = data.devoirs;
  const prochain = aRendre[0];
  return (
    <p className="max-w-xl text-sm text-white/70 sm:text-base">
      {jours > 0 ? (
        <>
          Vous avez cours <strong className="text-white">{pluriel(jours, 'jour')}</strong> cette semaine
        </>
      ) : (
        'Pas de cours prévu d’ici dimanche'
      )}
      {nombre > 0 && prochain ? (
        <>
          {' '}
          et <strong className="text-white">{pluriel(nombre, 'devoir')} à rendre</strong>
          {prochain.enRetard ? ', dont un en retard depuis ' : ', le prochain pour '}
          <strong className="text-codialis-mint">{jourEcheance(prochain.echeance)}</strong>.
        </>
      ) : (
        <> et aucun devoir à rendre.</>
      )}
    </p>
  );
}

function CarteSeance({ seance }: { seance: SeanceApprenant }): JSX.Element {
  const navigate = useNavigate();
  // Une classe à distance s'émarge dans sa salle, caméra allumée : on y mène.
  const aDistance = seance.type === 'ELEARNING';
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-100 p-3 transition hover:bg-gray-50 sm:flex-row sm:items-center sm:gap-4">
      <div className="gradient-mint flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white">
        <Icone nom={aDistance ? 'film' : 'stylo'} className="h-6 w-6" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-codialis-navy">{seance.libelle}</div>
        <div className="text-xs text-codialis-gray">
          {dateCourte(seance.date)} · {seance.demiJournee} · {seance.heureDebut}–
          {seance.heureFin}
          {seance.lieu ? ` · ${seance.lieu}` : ''}
          {seance.formateur ? ` · ${seance.formateur}` : ''}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Pastille texte={seance.statutTexte} ton={seance.statutTon} />
        {seance.signeLe ? (
          <span className="text-xs text-codialis-gray">
            Émargé le {heureCourte(seance.signeLe)}
          </span>
        ) : seance.emargeable ? (
          <button
            type="button"
            onClick={() => navigate(aDistance ? '/player?onglet=classes' : '/emargement')}
            className="min-h-[40px] rounded-lg bg-codialis-mint px-3 py-2 text-xs font-semibold text-codialis-navy transition hover:bg-codialis-teal"
          >
            {aDistance ? 'Rejoindre la classe' : 'Émarger'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** Bloc « Reprendre votre formation » : la prochaine leçon à suivre, d'un geste. */
function CarteReprise({ reprise }: { reprise: RepriseFormation }): JSX.Element {
  const navigate = useNavigate();
  const { cours } = reprise;
  const commence = cours?.regardeLe !== null && cours?.regardeLe !== undefined;
  const verbe = commence ? 'Reprendre' : 'Commencer';

  return (
    <section aria-labelledby="titre-reprise" className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 id="titre-reprise" className="font-display text-lg font-bold text-codialis-navy">
            {cours && !commence ? 'Commencer votre formation' : 'Reprendre votre formation'}
          </h2>
          <p className="text-sm text-codialis-gray">
            {cours?.regardeLe
              ? `Là où vous vous êtes arrêté(e) · ${delai(-joursDepuis(cours.regardeLe))}`
              : 'Vos cours en ligne, leçon par leçon'}
          </p>
        </div>
        <Link
          to="/player"
          className="inline-flex items-center text-sm font-medium text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
        >
          Voir tous mes cours
        </Link>
      </div>

      {reprise.refus ? (
        <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">{reprise.refus}</p>
      ) : !cours ? (
        <p className="text-sm text-codialis-gray">
          Aucun cours en ligne n’est encore publié pour{' '}
          {reprise.formation ? `« ${reprise.formation} »` : 'votre formation'}.
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100 transition hover:shadow-card">
          <div className="grid grid-cols-1 md:grid-cols-5">
            <div className="video-area relative flex h-44 items-center justify-center md:col-span-2 md:h-auto md:min-h-[12rem]">
              <button
                type="button"
                onClick={() => navigate(adresseLecon(cours.lecon.id))}
                aria-label={`${verbe} la leçon ${cours.lecon.numero} · ${cours.lecon.titre}`}
                className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-codialis-navy shadow-xl transition hover:bg-white"
              >
                <IconePleine nom="lecturePleine" className="ml-1 h-7 w-7" />
              </button>
              <div className="absolute left-3 top-3 max-w-[calc(100%-1.5rem)] truncate rounded-md bg-black/40 px-2.5 py-1 text-xs font-medium text-white backdrop-blur">
                Module {cours.lecon.module.numero}
                {cours.lecon.video ? ` · ${dureeLisible(cours.lecon.dureeSecondes) ?? 'Vidéo'}` : ' · Sans vidéo'}
              </div>
              {cours.lecon.positionSecondes !== null && (
                <div className="absolute bottom-3 right-3 rounded-md bg-black/40 px-2.5 py-1 text-xs text-white backdrop-blur">
                  {tempsLisible(cours.lecon.positionSecondes)}
                  {cours.lecon.dureeSecondes ? ` / ${tempsLisible(cours.lecon.dureeSecondes)}` : ''}
                </div>
              )}
            </div>
            <div className="min-w-0 p-4 sm:p-5 md:col-span-3">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                {reprise.formation && (
                  <span className="badge-info max-w-full truncate rounded px-2 py-0.5 text-xs font-medium">
                    {reprise.formation}
                  </span>
                )}
                {cours.bloc && (
                  <span className="text-xs text-codialis-gray">
                    Bloc {cours.bloc.numero} / {cours.bloc.total}
                  </span>
                )}
                <span className="text-xs text-codialis-gray">
                  Leçon {cours.lecon.rang} sur {cours.nombreLecons}
                </span>
              </div>
              <h3 className="font-display mb-1.5 text-lg font-bold text-codialis-navy">{cours.titre}</h3>
              <p className="mb-4 text-sm text-codialis-gray">
                Module {cours.lecon.module.numero} : <strong>{cours.lecon.module.titre}</strong> · Leçon{' '}
                {cours.lecon.numero} · {cours.lecon.titre}
              </p>
              {cours.avancement.taux !== null && (
                <div className="mb-3">
                  <div className="mb-1.5 flex items-center justify-between text-xs">
                    <span className="text-codialis-gray">Progression du cours</span>
                    <span className="font-semibold text-codialis-navy">{tauxLisible(cours.avancement)}</span>
                  </div>
                  <BarreAvancement avancement={cours.avancement} libelle={`Progression du cours ${cours.titre}`} />
                </div>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-1.5 text-xs text-codialis-gray">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-[10px] font-bold text-white">
                    {initiales(cours.auteur)}
                  </div>
                  <span className="min-w-0 truncate">{cours.auteur}</span>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(adresseLecon(cours.lecon.id))}
                  className="flex min-h-[44px] items-center gap-2 rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep"
                >
                  <IconePleine nom="lecturePleine" className="h-4 w-4" />
                  {verbe}
                  {cours.lecon.positionSecondes !== null ? ` à ${tempsLisible(cours.lecon.positionSecondes)}` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const STYLE_GENRE: Record<CoursAVenir['genre'], { pave: string; ton: string }> = {
  PRESENTIEL: { pave: 'gradient-mint text-white', ton: 'success' },
  CENTRE_FORMATION: { pave: 'gradient-mint text-white', ton: 'success' },
  A_DISTANCE: { pave: 'bg-codialis-sky text-white', ton: 'info' },
  EPREUVE_ECRITE: { pave: 'bg-amber-500 text-white', ton: 'warning' },
  ECF: { pave: 'bg-amber-500 text-white', ton: 'warning' },
};

function LigneCours({ cours }: { cours: CoursAVenir }): JSX.Element {
  const [annee, mois, jour] = cours.date.split('-').map(Number);
  const nomMois = new Date(Date.UTC(annee ?? 1970, (mois ?? 1) - 1, jour ?? 1)).toLocaleDateString('fr-FR', {
    timeZone: 'UTC',
    month: 'short',
  });
  const quand = cours.joursRestants <= 1 ? `${delai(cours.joursRestants).replace(/^./, (c) => c.toUpperCase())} · ` : '';
  const horaire = cours.heureDebut && cours.heureFin ? `${cours.heureDebut}–${cours.heureFin}` : 'Journée';
  const style = STYLE_GENRE[cours.genre];
  const contenu = (
    <>
      <div className={`${style.pave} flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl`}>
        <div className="text-xl font-bold leading-none">{jour}</div>
        <div className="text-[10px] uppercase tracking-wider">{nomMois}</div>
      </div>
      {/* Sur téléphone, l'horaire passe sous l'intitulé plutôt que de l'écraser. */}
      <div className="min-w-0 flex-1 sm:flex sm:items-center sm:gap-4">
        <div className="min-w-0 sm:flex-1">
          <div className="text-sm font-semibold text-codialis-navy">{cours.libelle}</div>
          <div className="text-xs text-codialis-gray">
            {[cours.lieu, cours.formateur].filter(Boolean).join(' · ') || dateLongue(cours.date)}
          </div>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 sm:mt-0 sm:block sm:shrink-0 sm:text-right">
          <div className="text-sm font-medium text-codialis-navy">
            {quand}
            {horaire}
          </div>
          <Pastille texte={cours.genreLibelle} ton={style.ton} />
        </div>
      </div>
    </>
  );
  const classes = 'flex items-center gap-3 rounded-xl p-3 transition hover:bg-gray-50 sm:gap-4';
  // Une classe à distance se rejoint depuis le lecteur.
  return cours.genre === 'A_DISTANCE' ? (
    <Link to="/player?onglet=classes" className={classes}>
      {contenu}
    </Link>
  ) : (
    <div className={classes}>{contenu}</div>
  );
}

function LigneDevoir({ devoir }: { devoir: DevoirApprenant }): JSX.Element {
  return (
    <Link
      to={adresseDevoir(devoir)}
      className="flex items-center gap-3 rounded-xl border border-gray-100 p-3 transition hover:border-codialis-mint/40"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-codialis-navy">{devoir.titre}</div>
        <div className="text-xs text-codialis-gray">{echeanceLongue(devoir.echeance)}</div>
      </div>
      <Pastille {...pastilleDevoir(devoir)} />
    </Link>
  );
}

function LigneEcheance({ echeance }: { echeance: EcheanceApprenant }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 p-3">
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-codialis-navy">{echeance.libelle}</div>
        <div className="text-xs text-codialis-gray">{dateCourte(echeance.date)}</div>
      </div>
      <Pastille texte={delai(echeance.joursRestants)} ton={tonEcheance(echeance.joursRestants)} />
    </div>
  );
}

function Chiffre({
  vers,
  libelle,
  valeur,
  detail,
  icone,
  teinte,
}: {
  vers: string;
  libelle: string;
  valeur: string;
  detail: string;
  icone: Parameters<typeof Icone>[0]['nom'];
  teinte: string;
}): JSX.Element {
  return (
    <Link to={vers} className="stat-card block rounded-2xl bg-white p-4 shadow-card sm:p-5">
      <div className="mb-3 flex items-start justify-between">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${teinte}`}>
          <Icone nom={icone} className="h-5 w-5" />
        </div>
        <Icone nom="chevronDroit" className="h-4 w-4 text-codialis-gray" />
      </div>
      <div className="text-xs font-medium uppercase tracking-wide text-codialis-gray">{libelle}</div>
      <div className="font-display mt-1 text-3xl font-extrabold text-codialis-navy">{valeur}</div>
      <div className="mt-1 text-xs text-codialis-gray">{detail}</div>
    </Link>
  );
}

export function DashboardApprenantPage(): JSX.Element {
  const { utilisateur } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, error } = useTableauBordApprenant();
  // Les notifications de l'utilisateur sont sa seule activité réellement tracée.
  const { data: notificationsRes } = useNotifications({});

  if (isLoading) return <Chargement libelle="Chargement de votre espace…" />;

  if (error) {
    const message =
      error instanceof ErreurApi
        ? error.message
        : "Votre espace n'a pas pu être chargé. Réessayez plus tard.";
    return (
      <div className="mx-auto max-w-[1500px] p-4 sm:p-6">
        <MessageErreur message={message} />
      </div>
    );
  }

  if (!data) return <div />;

  const { dossier, emargement, seancesDuJour, documents, pieces, reprise, completion, devoirs, prochainsCours, ecf } = data;
  const notifications = notificationsRes?.notifications.slice(0, 6) ?? [];
  const piecesAFournir = pieces.filter(
    (piece) => piece.statut === 'ATTENDUE' || piece.statut === 'REFUSEE',
  );
  const prochainDevoir = devoirs.aRendre[0];

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <section className="gradient-card relative overflow-hidden rounded-2xl p-4 text-white sm:p-6 lg:p-8">
        <div className="absolute -right-10 -top-10 h-64 w-64 rounded-full bg-codialis-mint/10 blur-3xl" />
        <div className="absolute bottom-0 right-20 h-48 w-48 rounded-full bg-codialis-sky/20 blur-3xl" />
        <div className="relative flex flex-col items-start justify-between gap-5 lg:flex-row lg:items-center">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wider text-codialis-mint">
              {dateLongue(jourReunion())}
            </div>
            <h1 className="font-display mb-2 mt-1 text-2xl font-extrabold sm:text-3xl lg:text-4xl">
              Bonjour {utilisateur?.prenom ?? dossier.prenom}
            </h1>
            <PhraseAccueil data={data} />
            <p className="mt-2 max-w-xl text-xs text-white/60">
              {dossier.formation ?? 'Formation non renseignée à votre dossier'}
              {dossier.session ? ` · ${dossier.session}` : ''}
              {dossier.dateFinPrevue ? ` · fin prévue le ${dateCourte(dossier.dateFinPrevue)}` : ''}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Pastille texte={dossier.statutTexte} ton={dossier.statutTon} />
              <span className="text-xs text-white/60">
                {dossier.site} · {dossier.mode === 'CFA' ? 'Apprentissage' : 'Formation continue'}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {reprise.cours && (
              <button
                type="button"
                onClick={() => navigate(adresseLecon(reprise.cours!.lecon.id))}
                className="flex min-h-[44px] items-center gap-2 rounded-xl bg-codialis-mint px-5 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-codialis-teal"
              >
                <IconePleine nom="lecturePleine" className="h-4 w-4" />
                {reprise.cours.regardeLe ? 'Reprendre ma formation' : 'Commencer ma formation'}
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/emargement')}
              className="flex min-h-[44px] items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-5 py-2.5 text-sm font-medium text-white backdrop-blur transition hover:bg-white/20"
            >
              <Icone nom="stylo" className="h-4 w-4 text-codialis-mint" />
              Mon émargement
            </button>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Chiffre
          vers="/mycourses"
          libelle="Taux de complétion"
          valeur={completion && completion.taux !== null ? tauxLisible(completion) : '—'}
          detail={
            !completion
              ? 'cours en ligne pas encore ouverts'
              : completion.taux === null
                ? 'aucun cours en ligne à suivre'
                : `${completion.faits} sur ${completion.total} leçons et quiz`
          }
          icone="graphique"
          teinte="bg-codialis-mint/10 text-codialis-mint"
        />
        <Chiffre
          vers="/emargement"
          libelle="Heures émargées"
          valeur={heures(emargement.heuresSuivies)}
          detail={
            emargement.tauxPresence === null
              ? 'aucune demi-journée pointée'
              : `sur ${heures(emargement.heuresPrevues)} tenues · ${pourcentagePresence(emargement.tauxPresence)} de présence`
          }
          icone="horloge"
          teinte="bg-codialis-sky/10 text-codialis-sky"
        />
        <Chiffre
          vers="/assignments"
          libelle="Devoirs à rendre"
          valeur={String(devoirs.nombre)}
          detail={
            !prochainDevoir
              ? 'rien à rendre pour le moment'
              : prochainDevoir.enRetard
                ? 'dont au moins un en retard'
                : `le prochain ${delai(prochainDevoir.joursRestants)}`
          }
          icone="presse"
          teinte="bg-amber-100 text-amber-600"
        />
        <Chiffre
          vers="/mycourses"
          libelle="ECF acquises"
          valeur={ecf ? String(ecf.acquises) : '—'}
          detail={
            !ecf
              ? 'pas d’ECF à votre programme'
              : ecf.realisees === 0
                ? `aucun bloc évalué sur ${ecf.attendues}`
                : `sur ${pluriel(ecf.realisees, 'bloc')} évalué${ecf.realisees > 1 ? 's' : ''}`
          }
          icone="medaille"
          teinte="bg-codialis-mint/10 text-codialis-mint"
        />
      </div>

      {/* Tests de positionnement attribués : la carte n'apparaît que s'il y
          en a, et se passe d'ici comme sur la tablette du centre. */}
      <MesTestsPositionnement />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <CarteReprise reprise={reprise} />

          <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="font-display text-lg font-bold text-codialis-navy">
                  Mes séances du jour
                </h2>
                <p className="text-sm text-codialis-gray">{dateLongue(jourReunion())}</p>
              </div>
              <Link to="/emargement" className="inline-flex items-center text-sm font-medium text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]">
                Historique
              </Link>
            </div>

            {seancesDuJour.length > 0 ? (
              <div className="space-y-3">
                {seancesDuJour.map((seance) => (
                  <CarteSeance key={seance.presenceId} seance={seance} />
                ))}
              </div>
            ) : (
              <div className="text-sm text-codialis-gray">Aucune séance ouverte aujourd’hui.</div>
            )}
          </div>

          <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-display text-lg font-bold text-codialis-navy">Mes prochains cours</h2>
              <Link
                to="/calendar"
                className="inline-flex items-center text-sm font-medium text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
              >
                Calendrier complet
              </Link>
            </div>
            {prochainsCours.cours.length > 0 ? (
              <div className="space-y-1">
                {prochainsCours.cours.map((cours) => (
                  <LigneCours key={cours.cle} cours={cours} />
                ))}
              </div>
            ) : (
              <div className="text-sm text-codialis-gray">
                Aucun cours n’est encore programmé à votre planning.
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-display text-lg font-bold text-codialis-navy">
                Mes documents
              </h2>
              <Link
                to="/app-documents"
                className="inline-flex items-center text-sm font-medium text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
              >
                Tous mes documents
              </Link>
            </div>

            {documents.length > 0 ? (
              <div className="space-y-2">
                {/* Les cinq plus récents ; la liste entière est dans « Mes documents ». */}
                {documents.slice(0, 5).map((document) => (
                  <div
                    key={document.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-100 p-3 transition hover:border-codialis-mint/40"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <Icone nom="document" className="h-5 w-5 shrink-0 text-codialis-mint" />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-codialis-navy">
                          {document.intitule}
                        </div>
                        <div className="text-xs text-codialis-gray">
                          {document.recuLe
                            ? `Reçu le ${dateCourte(document.recuLe)}`
                            : document.etatTexte}
                          {document.nouveau ? ' · nouveau' : ''}
                        </div>
                      </div>
                    </div>
                    {document.etat === 'A_SIGNER' ? (
                      <Link
                        to="/app-documents"
                        className="flex min-h-[40px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-codialis-sky hover:underline"
                      >
                        <Icone nom="stylo" className="h-4 w-4" />
                        À signer
                      </Link>
                    ) : document.telechargeable ? (
                      <a
                        href={urlDocumentApprenant(document.id)}
                        className="flex min-h-[40px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-codialis-sky hover:underline"
                      >
                        <Icone nom="telecharger" className="h-4 w-4" />
                        Télécharger
                      </a>
                    ) : (
                      <span className="text-xs text-codialis-gray">Fichier indisponible</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-codialis-gray">
                Aucun document n’est encore mis à votre disposition.
              </div>
            )}
          </div>
        </div>

        <div className="min-w-0 space-y-6">
          <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="font-display text-lg font-bold text-codialis-navy">Devoirs à rendre</h2>
              {devoirs.nombre > 0 && <Pastille texte={String(devoirs.nombre)} ton="warning" />}
            </div>
            {devoirs.aRendre.length > 0 ? (
              <div className="space-y-3">
                {devoirs.aRendre.map((devoir) => (
                  <LigneDevoir key={devoir.id} devoir={devoir} />
                ))}
              </div>
            ) : (
              <div className="text-sm text-codialis-gray">Aucun devoir à rendre pour le moment.</div>
            )}
            <Link
              to="/assignments"
              className="mt-4 flex min-h-[40px] w-full items-center justify-center text-sm font-medium text-codialis-sky hover:underline"
            >
              Voir tous mes devoirs
            </Link>
          </div>

          <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
            <h2 className="font-display mb-4 text-lg font-bold text-codialis-navy">
              Mes échéances
            </h2>
            {data.echeances.length > 0 ? (
              <div className="space-y-3">
                {data.echeances.map((echeance) => (
                  <LigneEcheance key={echeance.cle} echeance={echeance} />
                ))}
              </div>
            ) : (
              <div className="text-sm text-codialis-gray">
                Aucune échéance n’est encore fixée à votre dossier.
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="font-display text-lg font-bold text-codialis-navy">
                Pièces à déposer
              </h2>
              {piecesAFournir.length > 0 && (
                <Pastille texte={String(piecesAFournir.length)} ton="warning" />
              )}
            </div>
            {piecesAFournir.length > 0 ? (
              <div className="space-y-3">
                {piecesAFournir.map((piece) => (
                  <div
                    key={piece.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 p-3"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-codialis-navy">
                        {piece.libelle}
                      </div>
                      {piece.motifRefus && (
                        <div className="text-xs text-red-600">{piece.motifRefus}</div>
                      )}
                    </div>
                    <Pastille texte={piece.statutTexte} ton={piece.statutTon} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-codialis-gray">
                Toutes vos pièces ont été déposées.
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="font-display text-lg font-bold text-codialis-navy">
                Activité récente
              </h2>
              <Link
                to="/notifications"
                className="inline-flex items-center text-sm font-medium text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
              >
                Tout voir
              </Link>
            </div>
            {notifications.length > 0 ? (
              <div className="space-y-4">
                {notifications.map((notification) => (
                  <div key={notification.id} className="flex items-start gap-3">
                    <div
                      className={`mt-2 h-2 w-2 shrink-0 rounded-full ${
                        notification.lu ? 'bg-gray-300' : 'bg-codialis-mint'
                      }`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-codialis-navy">
                        {notification.titre}
                      </div>
                      <div className="text-xs text-codialis-gray">{notification.corps}</div>
                      <div className="mt-0.5 text-[10px] text-codialis-gray/70">
                        {heureCourte(notification.envoyeeLe)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-codialis-gray">Aucune notification pour l’instant.</div>
            )}
          </div>

          <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
            <h2 className="font-display mb-3 text-sm font-bold uppercase tracking-wider text-codialis-gray">
              Accès directs
            </h2>
            {/* Une colonne quand la colonne de droite est étroite : « Questionnaires » y tient entier. */}
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-1 2xl:grid-cols-2">
              {(
                [
                  ['/player', 'film', 'Cours en ligne', 'text-codialis-sky'],
                  ['/assignments', 'presse', 'Devoirs', 'text-amber-500'],
                  ['/emargement', 'stylo', 'Émargement', 'text-codialis-sky'],
                  ['/app-documents', 'document', 'Documents', 'text-codialis-mint'],
                  ['/app-questionnaires', 'liste', 'Questionnaires', 'text-amber-500'],
                  ['/messages', 'message', 'Messages', 'text-codialis-teal'],
                ] as const
              ).map(([vers, icone, libelle, teinte]) => (
                <Link
                  key={vers}
                  to={vers}
                  className="flex min-h-[44px] min-w-0 items-center gap-2 rounded-xl border border-gray-100 p-2.5 text-xs font-semibold text-codialis-navy transition hover:border-codialis-mint/40 hover:bg-gray-50"
                >
                  <Icone nom={icone} className={`h-4 w-4 shrink-0 ${teinte}`} />
                  <span className="min-w-0 truncate">{libelle}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
